import dynamic from 'next/dynamic';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { CLIENT_FORM, flowSchema } from '@common/schema/flow';
import { CreateProfileInput } from '@controller/schema';
import { cx } from '@emotion/css';
import { yupResolver } from '@hookform/resolvers/yup';

import { AddImagesVideos } from './AddImagesVideos';
import { DateAndTime } from './DateAndTime';
import styles from './FlowPage.module.scss';
import { GetRemoteQuote } from './GetRemoteQuote';
import { JobDescription } from './JobDescription';
import { PersonalInformation } from './PersonalInformation';
import { Pricing } from './Pricing';
import { ProblemCategory } from './ProblemCategory';
import { ProblemSubcategory } from './ProblemSubcategory';
import { PropertyAddress } from './PropertyAddress';
import { SelectCity } from './SelectCity';

const DynamicPaymentComponent = dynamic(
  () => import('@modules/payment/PaymentConnector'),
  {
    ssr: true,
  },
);
interface Props {
  defaultValues: any;
  onSubmit: (values: Partial<CreateProfileInput>) => Promise<void>;
}
export const FlowPage = (props: Props) => {
  const { defaultValues } = props;
  const [step, setStep] = useState(0);

  const el = useRef<HTMLDivElement>(null);
  let stepCount = -1;

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(flowSchema, {
      abortEarly: false,
    }),
    mode: 'all',
  });

  const { handleSubmit, watch } = methods;
  const problemType = watch(CLIENT_FORM.PROBLEM_CATEGORY_ID);
  const subProblemType = watch(CLIENT_FORM.PROBLEM_SUB_CATEGORY_ID);

  const city = watch(CLIENT_FORM.CITY);

  useEffect(() => {
    el?.current?.scrollIntoView({
      block: 'end',
      behavior: 'smooth',
      inline: 'end',
    });
  }, [step]);

  const onNext = (localStepCount: number) => {
    setStep(localStepCount + 1);
  };
  const onSubmit = (values: any) => {
    props.onSubmit(values);
  };

  // console.log(subProblemType);
  return (
    <div ref={el}>
      <div className={styles.body} style={{ paddingBottom: 40 }}>
        <div className={cx(styles.root, styles.root_form)}>
          <section className={cx(styles.sec, styles.form_wrpr)}>
            <div className={cx(styles.container, styles.inner)}>
              <div className={styles.row}>
                <div className={cx(styles.heading_blk, styles.text_center)}>
                  <h2 className={cx(styles.h2, styles.title)}>
                    Select the service you need
                  </h2>
                  <p className={styles.p}>
                    Get a fair price for home services in your area
                  </p>
                </div>
                <FormProvider {...methods}>
                  <div className={styles.form_inner}>
                    <ProblemCategory
                      step={step}
                      onNext={onNext}
                      stepCount={++stepCount}
                      styles={styles}
                    />
                    {problemType && step >= ++stepCount && (
                      <GetRemoteQuote
                        step={step}
                        onNext={onNext}
                        stepCount={++stepCount}
                        problemType={problemType}
                        styles={styles}
                      />
                    )}

                    <ProblemSubcategory
                      step={step}
                      onNext={onNext}
                      stepCount={++stepCount}
                      styles={styles}
                    />

                    {!defaultValues?.city && step >= ++stepCount && (
                      <SelectCity
                        styles={styles}
                        step={step}
                        onNext={onNext}
                        stepCount={++stepCount}
                      />
                    )}

                    {city && subProblemType && (
                      <Pricing
                        step={step}
                        onNext={onNext}
                        stepCount={++stepCount}
                        styles={styles}
                      />
                    )}
                    <DateAndTime
                      step={step}
                      onNext={onNext}
                      stepCount={++stepCount}
                      styles={styles}
                      minimumSlots={1}
                    />
                    {step >= ++stepCount && (
                      <JobDescription
                        step={step}
                        onNext={onNext}
                        stepCount={stepCount}
                        styles={styles}
                      />
                    )}
                    <AddImagesVideos
                      step={step}
                      onNext={onNext}
                      stepCount={++stepCount}
                      styles={styles}
                    />
                    <PersonalInformation
                      step={step}
                      onNext={onNext}
                      stepCount={++stepCount}
                      styles={styles}
                    />
                    <PropertyAddress
                      step={step}
                      onNext={onNext}
                      stepCount={++stepCount}
                      styles={styles}
                    />
                    {step == stepCount + 1 && (
                      <div style={{ height: 600 }}>
                        {/* <StripePaymentContainer
                          styles={styles}
                          onSubmitForm={handleSubmit(onSubmit)}
                        /> */}
                        <DynamicPaymentComponent
                          step={step}
                          stepCount={++stepCount}
                          styles={styles}
                          onSubmitForm={handleSubmit(onSubmit)}
                        />
                      </div>
                    )}
                  </div>
                </FormProvider>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
